import { Head, Link } from "@inertiajs/react"
import {
  ArrowLeft,
  Building2,
  ExternalLink,
  GraduationCap,
  MapPin,
  Trophy,
} from "lucide-react"

import { ClaimProfileDialog } from "@/components/claim-profile-dialog"
import AppLayout from "@/layouts/app-layout"
import { rankingsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Developer {
  id: number
  name: string
  headline: string | null
  location: string | null
  score: number | null
  linkedin_url: string
  skills: string[] | null
  experience: { title: string; company: string }[] | null
  education: { school: string }[] | null
  company: {
    name: string
    tier: string
    tier_description: string
  } | null
}

interface Props {
  developer: Developer
}

export default function RankingsShow({ developer }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Rankings", href: rankingsPath() },
    { title: developer.name || "Developer", href: `/rankings/${developer.id}` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={developer.name || "Developer Profile"} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center gap-4">
          <Link
            href={rankingsPath()}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">
              {developer.name || "Unknown"}
            </h1>
            {developer.headline && (
              <p className="text-muted-foreground">{developer.headline}</p>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-xl border p-6">
              <h2 className="mb-4 text-lg font-semibold">Overview</h2>

              <div className="grid gap-4 sm:grid-cols-2">
                {developer.company && (
                  <div className="flex items-start gap-3">
                    <Building2 className="text-muted-foreground mt-0.5 h-5 w-5" />
                    <div>
                      <p className="font-medium">{developer.company.name}</p>
                      <p className="text-muted-foreground text-sm">
                        {developer.company.tier} -{" "}
                        {developer.company.tier_description}
                      </p>
                    </div>
                  </div>
                )}

                {developer.location && (
                  <div className="flex items-start gap-3">
                    <MapPin className="text-muted-foreground mt-0.5 h-5 w-5" />
                    <p>{developer.location}</p>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Trophy className="text-muted-foreground mt-0.5 h-5 w-5" />
                  <div>
                    <p className="font-medium">
                      Score: {Math.round(developer.score ?? 0)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <ExternalLink className="text-muted-foreground mt-0.5 h-5 w-5" />
                  <a
                    href={developer.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    LinkedIn Profile
                  </a>
                </div>
              </div>
            </div>

            {developer.skills && developer.skills.length > 0 && (
              <div className="rounded-xl border p-6">
                <h2 className="mb-4 text-lg font-semibold">Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {developer.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="bg-primary/10 text-primary rounded-full px-3 py-1 text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {developer.experience && developer.experience.length > 0 && (
              <div className="rounded-xl border p-6">
                <h2 className="mb-4 text-lg font-semibold">Experience</h2>
                <div className="space-y-4">
                  {developer.experience.map((exp, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Building2 className="text-muted-foreground mt-0.5 h-4 w-4" />
                      <div>
                        <p className="font-medium">{exp.title}</p>
                        {exp.company && (
                          <p className="text-muted-foreground text-sm">
                            {exp.company}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {developer.education && developer.education.length > 0 && (
              <div className="rounded-xl border p-6">
                <h2 className="mb-4 text-lg font-semibold">Education</h2>
                <div className="space-y-4">
                  {developer.education.map((edu, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <GraduationCap className="text-muted-foreground mt-0.5 h-4 w-4" />
                      <p className="font-medium">{edu.school}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-xl border p-6">
              <h2 className="mb-4 text-lg font-semibold">Score Breakdown</h2>
              <div className="space-y-4">
                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>Company Tier</span>
                    <span>50%</span>
                  </div>
                  <div className="bg-muted h-2 overflow-hidden rounded-full">
                    <div
                      className="bg-primary h-full"
                      style={{ width: `${(developer.score ?? 0) * 0.5}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>Experience</span>
                    <span>30%</span>
                  </div>
                  <div className="bg-muted h-2 overflow-hidden rounded-full">
                    <div
                      className="bg-primary h-full"
                      style={{ width: `${(developer.score ?? 0) * 0.3}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>Education</span>
                    <span>20%</span>
                  </div>
                  <div className="bg-muted h-2 overflow-hidden rounded-full">
                    <div
                      className="bg-primary h-full"
                      style={{ width: `${(developer.score ?? 0) * 0.2}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <a
              href={developer.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-primary-foreground hover:bg-primary/90 block rounded-lg px-4 py-3 text-center"
            >
              View Full Profile on LinkedIn
            </a>

            <div className="mt-4">
              <ClaimProfileDialog
                developerId={developer.id}
                developerName={developer.name || "Unknown"}
                variant="link"
              />
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
